import type { FC, ReactElement, ReactNode } from "react"

import { useTranslation } from "react-i18next"

import { type TScreenBreakpoints, type TUserState, useScreenBreakpoints, useUser } from "@/3514/hooks"
import { type TProductTypeQuery, useProductTypeQuery } from "@/3514/queries"
import { type IAppState, useAppSelector } from "@/3514/store/app"
import { buildCssClassName, isUserParticipant } from "@/3514/utils"
import { CARE_TEAM_EMAIL, ERROR_TEXT } from "@/constants"
import { SimpleLayout } from "@/layouts"
import ErrorTooltip from "@/shared/error-page/ErrorTooltip"
import OwlErrorLogo from "@/svgs/OwlErrorLogo"

type Props = {
    errorContent: ReactNode
}

/**
 * @deprecated uses legacy stuff and is used strange, remove A$AP
 */
export const ErrorPage: FC<Props> = ({ errorContent = ERROR_TEXT }: Props): ReactElement => {
    const { t } = useTranslation()

    const { data: productType, isFetching: isLoading, isError }: TProductTypeQuery = useProductTypeQuery()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const { isOpenSidebar }: IAppState = useAppSelector()

    const { user }: TUserState = useUser()

    const isParticipant: boolean = isUserParticipant(user)

    return (
        <SimpleLayout
            withProfileHeader
            key={isParticipant ? "sl1" : "sl2"}
            className={buildCssClassName("pb-[40px] md:pb-[100px]", [
                isParticipant && [
                    breakpoints.xs || breakpoints.sm ? "ml-[0px]" : isOpenSidebar ? "ml-[250px]" : "ml-[80px]"
                ]
            ])}
            _withoutLogo={isParticipant}
        >
            <div className="d-flex justify-content-center align-items-center">
                <div className="mt-4">
                    <ErrorTooltip>{errorContent}</ErrorTooltip>
                    <div className="mt-5 d-flex justify-content-center">
                        <OwlErrorLogo />
                    </div>
                </div>
                <div
                    className={buildCssClassName(
                        "help-text-bottom !bottom-[40px] md:!bottom-[100px] w-100 text-center",
                        [
                            isParticipant && [
                                breakpoints.xs || breakpoints.sm
                                    ? "ml-[0px]"
                                    : isOpenSidebar
                                      ? "ml-[250px] !translate-x-[-60%]"
                                      : "ml-[80px] !translate-x-[-55%]"
                            ]
                        ]
                    )}
                >
                    <span className="color-gray">{t("Need help?")} </span>
                    {(!isLoading && !productType) || isError ? (
                        <a href={CARE_TEAM_EMAIL} className="color-brand font-bold">
                            {t("Contact Wiser Care Team")}
                        </a>
                    ) : (
                        <a href={`mailto:${productType?.supportEmail}`} className="color-brand font-bold">
                            {t("Contact")} {productType?.supportTeamName}
                        </a>
                    )}
                </div>
            </div>
        </SimpleLayout>
    )
}
